<template>
  <b-modal
    id="shareChild"
    size="md"
    centered
    :hide-header="true"
    :hide-footer="true"
    content-class="p-2 atm-rounded"
    no-close-on-backdrop
  >
    <h3 class="mb-4">Share child</h3>
    <b-form
      @submit.prevent="shuldBeShared(willBeShared)"
      class="d-flex flex-wrap gap-2 justify-content-center align-items-center px-3 mb-4 mt-4"
    >
      <b-alert show :variant="message.variant" class="small p-2 mb-3 w-100" v-if="message.show">
        <p v-if="message.title" class="font-weight-bold">{{ message.title }}</p>
        {{ message.text }}
      </b-alert>
      <input
        type="text"
        class="atm-input flex-grow-1 w-100"
        placeholder="Parent email to share"
        v-model="parentData.email"
        :disabled="disableEmailInput"
      />
      <div
        v-if="willBeShared"
        class="share-child-containe-form__childs-list share-child-containe-childs-list mt-1 w-100 d-flex flex-wrap gap-2 justify-content-center align-items-center justify-content-between"
      >
        <input
          type="text"
          class="atm-input flex-grow-1 w-25"
          placeholder="Parent name"
          v-model="parentData.name"
          :disabled="disableExtraInputs"
          required
        />
        <input
          type="text"
          class="atm-input flex-grow-1 w-25"
          placeholder="Parent last name"
          v-model="parentData.lastName"
          :disabled="disableExtraInputs"
        />
        <label for="selectChild" class="text-muted small m-0">Choose children to share</label>
        <select id="selectChild" class="atm-input w-100" v-model="selectedChildren">
          <option :value="0">Select child to share</option>
          <option v-for="(child, index) in childrenData" :key="index" :value="child.id">
            {{ child.name }} {{ child.last_name }}
          </option>
        </select>
      </div>
      <div class="d-flex gap-4 justify-content-center align-items-center w-100 mt-3">
        <b-button
          type="button"
          class="atm-button  bg-gray border-0 text-dark font-weight-bold w-100"
          @click="hideUpdateChecklistModal"
        >
          Back
        </b-button>
        <b-button type="submit" class="atm-button bg-main font-weight-bold w-100" :disabled="emailError">
          <b-spinner v-if="loaderShare" label="Save" />
          <span v-else>{{ textConfirmButton }}</span>
        </b-button>
      </div>
    </b-form>
  </b-modal>
</template>

<script>
import { useSessionStore } from '@/common';
import { getParentByEmail, setParentAssignChildren, setInviteParent } from '@/common/services';
import { validateEmail } from '@/auth/utils';

export default {
  props: {
    childrenData: {
      type: Array,
      default: () => {},
    },
  },
  data() {
    const sessionStore = useSessionStore();
    return {
      sessionStore,
      willBeShared: false,
      disableExtraInputs: false,
      disableEmailInput: false,
      parentToAssignId: 0,
      selectedChildren: 0,
      loaderShare: false,
      parentData: { email: '', name: '', lastName: '', canEdit: true },
      message: { show: false, title: '', text: '', variant: '' },
      emailError: false,
    };
  },
  computed: {
    parentId() {
      return this.sessionStore.userId;
    },
    textConfirmButton() {
      return this.willBeShared ? 'Share' : 'Next';
    },
  },
  methods: {
    hideUpdateChecklistModal() {
      this.$bvModal.hide('shareChild');
      this.resetData();
    },
    shuldBeShared(willBeShared) {
      willBeShared ? this.shareChild() : this.continueToShared();
    },
    errorsInEmailInput() {
      if (!this.parentData.email) {
        this.displayAlert({ text: 'Email is required', variant: 'danger' });
        return (this.emailError = true);
      }
      if (!validateEmail(this.parentData.email)) {
        this.displayAlert({ text: 'Invalid email', variant: 'danger' });
        return (this.emailError = true);
      }
      this.resetDisplayAlert();
    },
    setParent(parent) {
      this.disableExtraInputs = true;
      this.parentToAssignId = parent.id;
      this.parentData = {
        ...this.parentData,
        name: parent.name,
        lastName: parent.last_name,
      };
    },
    enableSharing() {
      this.willBeShared = true;
      this.disableEmailInput = true;
      this.loaderShare = false;
    },
    async continueToShared() {
      this.loaderShare = true;
      try {
        const parent = await getParentByEmail(this.parentData.email);
        this.setParent(parent);
      } catch (error) {
        this.displayAlert({
          title: 'Remember!',
          text: 'This change will not be made until approved by an administrator',
          variant: 'warning',
        });
      } finally {
        this.enableSharing();
      }
    },
    async shareChild() {
      this.resetDisplayAlert();
      const dataToInviteOrShare = {
        parentId: this.parentId,
        assignToParentId: this.parentToAssignId,
        childList: [this.selectedChildren],
        assign: true,
        ...this.parentData,
      };
      if (!this.selectedChildren) {
        this.displayAlert({ text: 'Please select a child to share', variant: 'warning' });
        return;
      }
      this.loaderShare = true;
      try {
        this.parentToAssignId
          ? await setParentAssignChildren(dataToInviteOrShare)
          : await setInviteParent(dataToInviteOrShare);
        this.hideUpdateChecklistModal();
      } catch (error) {
        this.displayAlert({ text: 'Something went wrong', variant: 'danger' });
      } finally {
        this.loaderShare = false;
      }
    },
    displayAlert({ title = '', text = '', variant = 'success' }) {
      this.message = {
        show: true,
        title,
        text,
        variant,
      };
    },
    resetDisplayAlert() {
      this.message = { show: false, title: '', text: '', variant: '' };
      this.emailError = false;
    },
    resetData() {
      this.willBeShared = false;
      this.disableExtraInputs = false;
      this.disableEmailInput = false;
      this.selectedChildren = 0;
      this.parentToAssignId = 0;
      this.parentData = {
        email: '',
        name: '',
        lastName: '',
        canEdit: true,
      };
      this.resetDisplayAlert();
    },
  },
  watch: {
    'parentData.email': {
      handler() {
        this.errorsInEmailInput();
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss"></style>

